// src/app/session-cleanup.ts
//
// What the app forgets when a session ends or another person signs in on the same browser. The query cache
// and a few stores hold one person's patient data in memory, and some keys in Web Storage hold patient or
// encounter ids. None of that may be there for the next person. Runs from a subscriber (like print-epoch.ts),
// so auth-store imports no feature module.
//
// Kept on purpose: UI preferences and flags (rail, panel fold, feature flags) and the stores that already key
// by user (patients-list-store, scheduling-store). The full list is in the PR that added this file.
import { useAuthStore } from '@/auth/auth-store';
import { queryClient } from '@/lib/query-client';
import { useUnsavedNote } from '@/encounter/services/unsaved-note';
import { STEP_PASS_KEY_PREFIX } from '@/encounter/lib/step-pass';
import { TICK_STORAGE_KEY } from '@/patients/lib/care-model-tasks';
import { SEEN_NEEDS_KEY } from '@/dashboard/ready-list/use-seen-needs';
import { useCreateAppointmentHandoff } from '@/shared/store/create-appointment-handoff';

export function clearSessionData(): void {
  queryClient.clear();
  // Flags and draft ids only, no note text; runs once the session is over, so the same person's leave guard never sees it.
  useUnsavedNote.getState().reset();
  useCreateAppointmentHandoff.getState().clear();
  try {
    const stale: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(STEP_PASS_KEY_PREFIX)) stale.push(k);
    }
    stale.forEach((k) => window.localStorage.removeItem(k));
    window.localStorage.removeItem(TICK_STORAGE_KEY);
    window.sessionStorage.removeItem(SEEN_NEEDS_KEY);
  } catch {
    // Storage blocked: nothing was written there either.
  }
}

if (typeof window !== 'undefined') {
  useAuthStore.subscribe((state, prev) => {
    const signedOut = prev.user !== null && state.user === null;
    const userChanged =
      prev.user !== null && state.user !== null && prev.user.userId !== state.user.userId;
    // A sign-in by another person than the one whose session ended here, on whatever page and with no
    // logout in between (the tab was closed): the leftovers belong to the earlier person.
    const newOwner = state.sessionOwnerChanged && !prev.sessionOwnerChanged;
    if (signedOut || userChanged || newOwner) clearSessionData();
  });
}
